import { jsx as _jsx } from "react/jsx-runtime";
import { ClipUtils } from "@air/utils-clips";
import { reportErrorToBugsnag } from "@air/utils-error";
import noop from "lodash/noop";
import { createContext, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { usePersistedState } from "~/hooks/usePersistedState";
import { isDevOrTestStage } from "~/swr-hooks/utils";
const REWIND_INTERVAL = 100;
const VIDEO_CONTENT_CONTROLS_CONTEXT_DEFAULT_VALUE = {
    isVideoLooping: false,
    setIsVideoLooping: noop,
    currentPlaybackSpeed: 1,
    setCurrentPlaybackSpeed: noop,
    volume: 0,
    muted: false,
    toggleMuted: noop,
    onVolumeChange: noop,
    setVideoElement: noop,
    videoElement: null,
    setVolume: noop,
    isPlaying: false,
    setIsPlaying: noop,
    isRewinding: false,
    play: noop,
    pause: noop,
    shouldResetVideo: false,
    setShouldResetVideo: noop,
    isProcessing: false,
    setIsProcessing: noop,
    onResetPlaybackStep: noop,
    setCurrentTime: noop,
    resolutionOptions: [],
    setResolutionOptions: noop,
    currentResolutionIndex: -1,
    setCurrentResolutionIndex: noop
};
export const VideoContentControlsContext = /*#__PURE__*/ createContext(VIDEO_CONTENT_CONTROLS_CONTEXT_DEFAULT_VALUE);
export const VideoContentControlsContextProvider = /*#__PURE__*/ memo((param)=>{
    let { children } = param;
    const [isVideoLooping, setIsVideoLooping] = useState(false);
    const [currentPlaybackSpeed, setCurrentPlaybackSpeed] = usePersistedState("air-playback-speed", 1);
    const [volume, setVolume] = usePersistedState("air-volume", 0.8);
    const [muted, setMuted] = usePersistedState("air-volume-muted", false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isRewinding, setIsRewinding] = useState(false);
    const [shouldResetVideo, setShouldResetVideo] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [resolutionOptions, setResolutionOptions] = useState([]);
    const [currentResolutionIndex, setCurrentResolutionIndex] = useState(-1);
    const videoElementRef = useRef(null);
    const setVideoElement = useCallback((element)=>{
        videoElementRef.current = element;
    }, []);
    const toggleMuted = useCallback(()=>{
        if (!videoElementRef.current) return null;
        videoElementRef.current.muted = !videoElementRef.current.muted;
        if (videoElementRef.current.muted) {
            setMuted(true);
        } else {
            setMuted(false);
            setVolume(videoElementRef.current.volume);
        }
    }, [
        setMuted,
        setVolume
    ]);
    const onVolumeChange = useCallback((value)=>{
        if (!videoElementRef.current) return null;
        videoElementRef.current.volume = value;
        setVolume(value);
    }, [
        setVolume
    ]);
    const onResetPlaybackStep = useCallback(()=>{
        setCurrentPlaybackSpeed(1);
    }, [
        setCurrentPlaybackSpeed
    ]);
    useEffect(()=>{
        if (currentPlaybackSpeed && currentPlaybackSpeed <= 0 && videoElementRef.current) {
            /**
       * We need to pause the video before we start rewinding it to avoid the video from
       * flickering at lower playback speeds.
       */ if (!videoElementRef.current.paused) {
                videoElementRef.current.pause();
            }
            const rewindInterval = setInterval(()=>{
                if (videoElementRef.current && videoElementRef.current.currentTime > 0) {
                    setIsRewinding(true);
                    ClipUtils.changeVideoTimeInSeconds(videoElementRef.current, REWIND_INTERVAL / 1000 * currentPlaybackSpeed);
                } else {
                    setIsRewinding(false);
                    clearInterval(rewindInterval);
                    onResetPlaybackStep();
                }
            }, REWIND_INTERVAL);
            return ()=>{
                setIsRewinding(false);
                clearInterval(rewindInterval);
            };
        }
    }, [
        currentPlaybackSpeed,
        onResetPlaybackStep
    ]);
    const play = useCallback(()=>{
        if (!videoElementRef.current) return;
        /**
     * When the video is first played, we need to reset the current time to 0.
     * Since we're streaming the video, each time hls.js loads the video, it
     * starts the video from where the current stream is. This means that if
     * the user mounts and unmounts the video, it'll start at a different time
     * each time.
     */ if (shouldResetVideo && isProcessing) {
            videoElementRef.current.currentTime = 0;
            setShouldResetVideo(false);
        }
        const playPromise = videoElementRef.current.play();
        if (playPromise) {
            playPromise.then(()=>{
                setIsPlaying(true);
            }).catch(()=>{
                // Handle autoplay being disabled
                setIsPlaying(false);
            });
        }
    }, [
        shouldResetVideo,
        isProcessing,
        setShouldResetVideo
    ]);
    const pause = useCallback(()=>{
        if (!videoElementRef.current) return;
        videoElementRef.current.pause();
        setIsPlaying(false);
    }, []);
    const setCurrentTime = useCallback((timeInSeconds)=>{
        if (!videoElementRef.current) return;
        videoElementRef.current.currentTime = timeInSeconds;
        // Force video to load the frame at the new time if paused
        if (videoElementRef.current.paused) {
            videoElementRef.current.play().catch(()=>{
            // Ignore errors if video is not ready
            });
            videoElementRef.current.pause();
        }
    }, []);
    const contextValue = useMemo(()=>({
            isVideoLooping,
            setIsVideoLooping,
            currentPlaybackSpeed,
            setCurrentPlaybackSpeed,
            volume,
            muted,
            toggleMuted,
            onVolumeChange,
            setVideoElement,
            videoElement: videoElementRef.current,
            setVolume,
            isPlaying,
            isRewinding,
            play,
            pause,
            shouldResetVideo,
            setShouldResetVideo,
            isProcessing,
            setIsProcessing,
            onResetPlaybackStep,
            setIsPlaying,
            setCurrentTime,
            resolutionOptions,
            setResolutionOptions,
            currentResolutionIndex,
            setCurrentResolutionIndex
        }), [
        isVideoLooping,
        setIsVideoLooping,
        currentPlaybackSpeed,
        setCurrentPlaybackSpeed,
        volume,
        muted,
        toggleMuted,
        onVolumeChange,
        setVideoElement,
        videoElementRef,
        setVolume,
        isPlaying,
        isRewinding,
        play,
        pause,
        shouldResetVideo,
        setShouldResetVideo,
        isProcessing,
        setIsProcessing,
        onResetPlaybackStep,
        setIsPlaying,
        setCurrentTime,
        resolutionOptions,
        setResolutionOptions,
        currentResolutionIndex,
        setCurrentResolutionIndex
    ]);
    return /*#__PURE__*/ _jsx(VideoContentControlsContext.Provider, {
        value: contextValue,
        children: children
    });
});
VideoContentControlsContextProvider.displayName = "VideoContentControlsContextProvider";
export const useVideoContentControlsContext = ()=>{
    const context = useContext(VideoContentControlsContext);
    if (context === VIDEO_CONTENT_CONTROLS_CONTEXT_DEFAULT_VALUE) {
        const error = new Error("useVideoContentControlsContext must be used within a VideoContentControlsContextProvider");
        if (isDevOrTestStage()) {
            throw error;
        } else {
            reportErrorToBugsnag({
                error,
                context: "VideoContentControlsContext not found"
            });
        }
    }
    return context;
};
